// i18n — string tables for nl, en, de and fr, plus the language context.
// Each language lives in src/lib/i18n/<lang>.js; every key must exist in all
// four tables (`npm run check:i18n`).
import { useCallback, useContext, createContext, useState, useEffect } from 'react';
import nl from './i18n/nl.js';
import en from './i18n/en.js';
import de from './i18n/de.js';
import fr from './i18n/fr.js';

export const I18N = { nl, en, de, fr };

export const LANGUAGES = [
  { code: 'nl', label: 'NL', name: 'Nederlands' },
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'de', label: 'DE', name: 'Deutsch' },
  { code: 'fr', label: 'FR', name: 'Français' },
];

const SUPPORTED = LANGUAGES.map((l) => l.code);
const DEFAULT_LANG = 'nl';

function readStoredLang() {
  try {
    const v = localStorage.getItem('lang');
    return SUPPORTED.includes(v) ? v : null;
  } catch {
    return null;
  }
}

// First supported language from the browser's preference list.
function browserLang() {
  if (typeof navigator === 'undefined') return null;
  const prefs = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const p of prefs) {
    const code = String(p || '').toLowerCase().split('-')[0];
    if (SUPPORTED.includes(code)) return code;
  }
  return null;
}

// Stored choice first, then the browser language, then Dutch.
export function initialLang() {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  return readStoredLang() || browserLang() || DEFAULT_LANG;
}

const LangContext = createContext({ lang: DEFAULT_LANG, setLang: () => {} });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(initialLang);
  const setLang = useCallback((v) => {
    if (!SUPPORTED.includes(v)) return;
    setLangState(v);
    try {
      localStorage.setItem('lang', v);
    } catch {
      /* storage unavailable: keep the choice for this session only */
    }
  }, []);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  return <LangContext.Provider value={{ lang, setLang }}>{children}</LangContext.Provider>;
}

export function useLang() {
  return useContext(LangContext);
}

// "{name}" placeholders in a string are filled from vars.
function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (m, key) => (vars[key] != null ? String(vars[key]) : m));
}

export function useT() {
  const { lang } = useContext(LangContext);
  return useCallback((k, vars) => t(lang, k, vars), [lang]);
}

// Standalone translator (for non-React code paths). Falls back to English,
// then Dutch, then the key itself.
export function t(lang, k, vars) {
  const str = I18N[lang]?.[k] ?? I18N.en[k] ?? I18N.nl[k] ?? k;
  return interpolate(str, vars);
}

// Picks "<key>.one" or "<key>.other" by count and fills {n}.
export function tp(lang, k, n, vars) {
  return t(lang, `${k}.${n === 1 ? 'one' : 'other'}`, { n, ...vars });
}

export function currentLang() {
  return readStoredLang() || browserLang() || DEFAULT_LANG;
}
